import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  adminService,
  type NavSettingRow,
  type NavVisibility,
} from '../../services/adminService'
import { eventService, type NavSummary } from '../../services/eventService'
import { useToast } from '../useToast'
import { apiError } from '../errors'
import { LoadingState } from '../../components/ui/States'

/**
 * Admin control over the built-in attendee nav items: per-item visibility
 * (Auto / Always show / Hidden) and drag-to-reorder. "Auto" shows the item only
 * when its section has content; the "has content" hint reflects the live event.
 * Home is always shown and isn't listed here.
 */

// Built-in items in their default order (Home omitted — always shown).
const BUILTIN_ITEMS: { key: string; label: string }[] = [
  { key: 'agenda', label: 'Agenda' },
  { key: 'speakers', label: 'Speakers' },
  { key: 'content', label: 'Content' },
  { key: 'forms', label: 'Forms' },
  { key: 'register', label: 'Register' },
  { key: 'info', label: 'Event Info' },
  { key: 'network', label: 'Network' },
  { key: 'badge', label: 'My badge' },
  { key: 'watch', label: 'Watch' },
]
const LABEL_BY_KEY = Object.fromEntries(BUILTIN_ITEMS.map((i) => [i.key, i.label]))

const VISIBILITY_OPTIONS: { value: NavVisibility; label: string }[] = [
  { value: 'auto', label: 'Auto (show if it has content)' },
  { value: 'show', label: 'Always show' },
  { value: 'hide', label: 'Hidden' },
]

export default function NavSettingsSection({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  // Ordered list of nav keys (drag order) + visibility per key.
  const [order, setOrder] = useState<string[]>(BUILTIN_ITEMS.map((i) => i.key))
  const [visByKey, setVisByKey] = useState<Record<string, NavVisibility>>({})
  const [summary, setSummary] = useState<NavSummary | null>(null)
  const [loading, setLoading] = useState(true)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  useEffect(() => {
    let cancelled = false
    Promise.all([
      adminService.getNavSettings(eventId).catch(() => [] as NavSettingRow[]),
      eventService.getNavSummary().catch(() => null),
    ])
      .then(([rows, sum]) => {
        if (cancelled) return
        const map: Record<string, NavVisibility> = {}
        for (const r of rows) map[r.navKey] = r.visibility
        setVisByKey(map)
        setSummary(sum)
        // Order: saved rows by ordinal first, then any built-ins without a row
        // in their default order.
        const saved = rows
          .filter((r) => LABEL_BY_KEY[r.navKey])
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((r) => r.navKey)
        const rest = BUILTIN_ITEMS.map((i) => i.key).filter(
          (k) => !saved.includes(k),
        )
        setOrder([...saved, ...rest])
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [eventId])

  const persist = async (nextOrder: string[], nextVis: Record<string, NavVisibility>) => {
    const settings: NavSettingRow[] = nextOrder.map((key, i) => ({
      navKey: key,
      visibility: nextVis[key] ?? 'auto',
      ordinal: i,
    }))
    try {
      await adminService.updateNavSettings(eventId, settings)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const changeVisibility = (key: string, visibility: NavVisibility) => {
    const next = { ...visByKey, [key]: visibility }
    setVisByKey(next)
    void persist(order, next)
  }

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return
    const from = order.indexOf(String(e.active.id))
    const to = order.indexOf(String(e.over.id))
    if (from < 0 || to < 0) return
    const next = arrayMove(order, from, to)
    setOrder(next)
    void persist(next, visByKey)
  }

  if (loading) return <LoadingState />

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Navigation menu
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Drag to reorder, and show / hide / auto-manage the built-in menu items.
        &quot;Auto&quot; hides an item when its section has no content.
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <Stack spacing={1}>
            {order.map((key) => (
              <SortableNavRow
                key={key}
                navKey={key}
                label={LABEL_BY_KEY[key] ?? key}
                hasContent={summary?.content?.[key] ?? true}
                visibility={visByKey[key] ?? 'auto'}
                onVisibilityChange={(v) => changeVisibility(key, v)}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  )
}

function SortableNavRow({
  navKey,
  label,
  hasContent,
  visibility,
  onVisibilityChange,
}: {
  navKey: string
  label: string
  hasContent: boolean
  visibility: NavVisibility
  onVisibilityChange: (v: NavVisibility) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: navKey })
  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        p: 1.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <IconButton size="small" sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIndicatorRoundedIcon fontSize="small" />
      </IconButton>
      <Typography fontWeight={600} sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Chip
        size="small"
        label={hasContent ? 'Has content' : 'Empty'}
        color={hasContent ? 'success' : 'default'}
        variant="outlined"
      />
      <TextField
        select
        size="small"
        value={visibility}
        onChange={(e) => onVisibilityChange(e.target.value as NavVisibility)}
        sx={{ minWidth: 220 }}
      >
        {VISIBILITY_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    </Card>
  )
}
